import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/oxid-zu-shopify";
const TITLE = "OXID eShop zu Shopify wechseln: Ablauf, Kosten, Datenübernahme";
const DESCRIPTION =
  "Von OXID eShop zu Shopify umziehen: Was sich übernehmen lässt, wo B2B-Logik und Module Aufwand machen, wie der Weiterleitungsplan aussieht und was der Wechsel kostet.";
const UPDATED = "2026-09-16";

const ANSWER =
  "Ein Wechsel von OXID eShop zu Shopify läuft über vier Stufen: Export von Produkten, Kunden und Bestellungen, Nachbau der Kategorie- und Variantenlogik in Shopify-Collections und Metafeldern, ein vollständiger 301-Plan für alle alten Adressen und ein gestaffelter Livegang. Üblich sind sechs bis zehn Wochen, weil OXID-Shops häufig B2B-Logik und eine Warenwirtschaft im Hintergrund haben.";

const faqs = [
  {
    q: "Lassen sich OXID-Daten nach Shopify übernehmen?",
    a: "Produkte mit Varianten, Bilder, Kunden und Bestellhistorie lassen sich per CSV oder direkt über die Datenbank übertragen. Kategoriebäume und Attribute bildet man in Shopify über Collections, Tags und Metafelder nach, eine direkte Entsprechung gibt es nicht.",
  },
  {
    q: "Was wird bei OXID-Shops meist unterschätzt?",
    a: "Die B2B-Seite. Kundengruppen, Staffelpreise, Nettodarstellung und freigeschaltete Preise sind in OXID Standard und in Shopify je nach Anforderung eine Frage von Shopify Plus, B2B-Funktionen oder eigener Logik. Das gehört vor dem Angebot geklärt, nicht danach.",
  },
  {
    q: "Was passiert mit meinen Rankings?",
    a: "Rankings bleiben erhalten, wenn jede alte OXID-Adresse per 301 ein passendes Ziel bekommt. Kritisch sind Kategorie-Adressen mit Filterparametern und Inhaltsseiten, die über Jahre Sichtbarkeit aufgebaut haben.",
  },
  {
    q: "Was kostet der Wechsel von OXID zu Shopify?",
    a: "Reine B2C-Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto. Mit ERP-Anbindung, Kundengruppen und Staffelpreisen liegt der Rahmen deutlich höher. Entwicklung berechne ich mit 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.",
  },
  {
    q: "Wie lange dauert die Migration?",
    a: "Sechs bis zehn Wochen sind üblich. Den Zeitplan bestimmen die angebundenen Systeme und die B2B-Preislogik, nicht die Zahl der Produkte.",
  },
];

export const Route = createFileRoute("/ratgeber/oxid-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="OXID eShop zu Shopify: der Umzug mit B2B-Logik"
      intro="OXID-Shops sind häufig B2B-Shops: Kundengruppen, Staffelpreise, Nettopreise und eine Warenwirtschaft im Hintergrund. Ein Wechsel zu Shopify ist deshalb selten ein reines Datenthema, sondern vor allem die Frage, wie diese Preislogik künftig abgebildet wird."
      source="ratgeber-oxid-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Warum Händler von OXID wechseln",
          p: [
            "Meist geht es um Verfügbarkeit von Hilfe. Der Markt an Entwicklern, die OXID sicher betreuen, ist klein geworden, und jede Anpassung braucht länger und kostet mehr als früher.",
            "Shopify ist in dieser Hinsicht der einfachere Weg: verbreitet, dokumentiert und ohne eigenes Hosting. Dafür ist der Checkout weniger frei anpassbar, was bei B2B vorher geprüft gehört.",
          ],
        },
        {
          h: "Was sich sauber übernehmen lässt",
          p: [],
          list: [
            "Produkte mit Varianten, Bildern, Preisen und Beständen",
            "Kundenkonten, Passwörter werden neu vergeben",
            "Bestellhistorie als Referenzdaten",
            "Inhaltsseiten und Blogbeiträge",
          ],
        },
        {
          h: "Was neu gedacht werden muss",
          p: [
            "Die B2B-Logik ist der eigentliche Projektinhalt. Erst wenn klar ist, welche Kundengruppen welche Preise sehen und ob Preise erst nach Freischaltung sichtbar sind, lässt sich der Aufwand seriös schätzen.",
          ],
          list: [
            "Kundengruppen und Staffelpreise neu abbilden",
            "Netto- und Bruttodarstellung je Kundentyp klären",
            "ERP- oder Warenwirtschaftsanbindung neu aufsetzen",
            "Attribute und Kategoriebäume auf Metafelder und Collections übertragen",
          ],
        },
        {
          h: "Der Weiterleitungsplan",
          p: [
            "Hier entscheidet sich, ob Sichtbarkeit erhalten bleibt. Ziehen Sie alle Adressen aus OXID und aus der Search Console, sortiert nach Sichtbarkeit und Umsatz, und geben Sie jeder einzelnen ein Ziel.",
          ],
          list: [
            "Produkt-, Kategorie- und Inhaltsadressen einzeln weiterleiten",
            "Filter- und Seitenzahl-Adressen mit Rankings nicht übersehen",
            "Sammelweiterleitungen auf die Startseite vermeiden",
            "Weiterleitungen vor dem Livegang testen",
          ],
        },
        {
          h: "Kostenrahmen",
          p: [
            "Reine B2C-Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto, mit ERP-Anbindung und B2B-Preislogik deutlich darüber. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto. Nach Sichtung des Shops gibt es ein festes Angebot.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/ratgeber/shopify-b2b-shop-einrichten", label: "Shopify B2B einrichten" },
        { to: "/ratgeber/shopware-zu-shopify", label: "Shopware zu Shopify" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
